import React from 'react'
import { ExhibitionArtist, ExhibitionArtistSection } from '../[slug]/components/artist-section'
import { ExhibitionPageDetailsFragment } from '@/gql/generated/graphql'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { BodyText } from 'design-configuration/components'

export const GroupExhibitionQ42024ArtistSection = ({
  exhibition
}: {
  exhibition: ExhibitionPageDetailsFragment
}): React.ReactElement => {
  const artists: ExhibitionArtist[] = []
  exhibition.Collections.forEach((collection) =>
    collection?.Collection?.Project?.artistPage?.data.forEach((artist) =>
      artists.push({ artist: artist.attributes, featuredToken: collection.Collection?.Project?.featured_token?.[0] })
    )
  )

  // sort artists by artist name
  artists.sort((a, b) => {
    if (!a?.artist || !b?.artist) {
      return 0
    }
    return a.artist.artistName > b.artist.artistName ? 1 : -1
  })

  const artistDisplayInfo = [
    {
      image1: getPrefixedLinkPath('/images/poetics/iskra/iskra-1.jpg'),
      image2: getPrefixedLinkPath('/images/poetics/iskra/iskra-2.jpg'),
      image3: getPrefixedLinkPath('/images/poetics/iskra/iskra-3.jpg'),
      content: (
        <div className="my-md">
          <div>
            <BodyText className="italic">b. 1988, Bulgaria</BodyText>
            <BodyText className="mb-sm italic">Lives and works in Madrid, Spain</BodyText>
          </div>
          <BodyText>
            Iskra Velitchkova is a Bulgarian artist based in Madrid with a technology and artificial intelligence
            background. Her work delves into the complex interactions between humans and machines, investigating how
            these dynamics can deepen our understanding of human limitations and potential. Working within the field of
            generative art, Velitchkova combines digital formats and physical media to explore new visual possibilities.
            She employs generative systems, artificial neural networks, and various mixed techniques including drawing,
            sculpture, and ceramics. Velitchkova has exhibited internationally at venues such as Galerie Greulich, MADS
            Gallery, and Sotheby&apos;s.
          </BodyText>
        </div>
      ),
      align: 'right',
      fullArtistDetails: artists[0]
    },
    {
      image1: getPrefixedLinkPath('/images/poetics/lars/lars-3.jpg'),
      image2: getPrefixedLinkPath('/images/poetics/lars/lars-2.jpg'),
      image3: getPrefixedLinkPath('/images/poetics/lars/lars-1.jpg'),
      content: (
        <div className="my-md">
          <div>
            <BodyText className="italic">b. Germany</BodyText>
            <BodyText className="mb-sm italic">Lives and works in New York City, USA</BodyText>
          </div>
          <BodyText>
            Lars Wander is a computer artist and programmer based in New York City, whose work explores the
            intersections of computation, perception, and generative systems. His practice centers on creating visual
            art exclusively through code, designing intricate software systems that generate unpredictable,
            algorithmically driven outcomes. Wander has exhibited internationally and is represented by ARTxCODE.
            Previously, he worked as a visualization specialist at Google, developing software to model global-scale
            storage systems; his related scientific research has been published in journals including IEEE. Wander has a
            longstanding passion for programming and regularly builds his tools using languages such as Javascript,
            WebGL2, and Julia to support his generative art practice.
          </BodyText>
        </div>
      ),
      align: 'left',
      fullArtistDetails: artists[1]
    },
    {
      image1: getPrefixedLinkPath('/images/poetics/zach/zach-1.jpg'),
      image2: getPrefixedLinkPath('/images/poetics/zach/zach-2.jpg'),
      image3: getPrefixedLinkPath('/images/poetics/zach/zach-3.jpg'),
      content: (
        <div className="my-md">
          <div>
            <BodyText className="italic">b. 1977, USA</BodyText>
            <BodyText className="mb-sm italic">Lives and works in New York City, USA</BodyText>
          </div>
          <BodyText>
            Zach Lieberman is a New York-based artist, educator, and programmer who integrates human gesture and
            interactive technology to create immersive, code-based installations and performances. His practice centers
            on transforming human input, such as drawing and movement, into dynamic visual and auditory expressions,
            revealing playful and unexpected forms of interaction. Lieberman&apos;s projects have garnered awards
            including the Golden Nica from Ars Electronica and Interactive Design of the Year from the Design Museum
            London, and his work has been featured in institutions such as New York&apos;s Museum of Modern Art. He
            holds a BA in Fine Arts from Hunter College and an MFA in Design and Technology from Parsons School of
            Design. Additionally, Lieberman co-created openFrameworks, an influential open-source C++ toolkit for
            creative coding, and co-founded the School for Poetic Computation, where he explores and teaches the
            expressive potential of code. He is an Adjunct Associate Professor of Media Arts and Sciences at the
            Massachusetts Institute of Technology&apos;s Media Lab. code. He is an Adjunct Associate Professor of Media
            Arts and Sciences at the Massachusetts Institute of Technology&apos;s Media Lab.
          </BodyText>
        </div>
      ),
      align: 'right',
      fullArtistDetails: artists[2]
    }
  ]

  return <ExhibitionArtistSection artistDisplayInfo={artistDisplayInfo} />
}
